/**
 * PaymentsPanel — file PMS paiements Stripe (60 derniers jours).
 * Batch #14.154 — extracted from AdminPmsQueuePage.jsx during refactor.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Banknote, Loader2, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";

export default function PaymentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pms/payments-overview?days=60`, { headers: authH() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (tx) => {
    if (!window.confirm(
      `Supprimer définitivement cette transaction de ${Number(tx.amount || 0).toLocaleString("fr-CA")} $ ?\n\n` +
      `Cette action retire le log local. La transaction Stripe reste intacte côté Stripe.`
    )) return;
    setDeletingId(tx.id);
    try {
      await axios.delete(`${API}/admin/pms/payments/${tx.id}`, { headers: authH() });
      toast.success("Transaction supprimée");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const items = data?.items || [];

  return (
    <>
      <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Banknote className="w-4 h-4 text-violet-600" />
          <h2 className="font-semibold text-slate-800 text-sm">Paiements Stripe (60 derniers jours)</h2>
        </div>
        {data && (
          <div className="flex gap-3 text-[11px]">
            <span className="text-emerald-700 font-mono"><b>Encaissé :</b> {data.total_paid_cad.toLocaleString("fr-CA")} $</span>
            {Object.entries(data.by_status || {}).map(([s, n]) => (
              <span key={s} className="text-slate-600">{s}: <b>{n}</b></span>
            ))}
          </div>
        )}
      </div>
      <div className="p-3">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400 italic">Aucun paiement récent</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((tx, idx) => {
              const isPaid = tx.payment_status === "paid";
              const isFailed = tx.payment_status === "failed";
              return (
                <div key={tx.id || `pay-${idx}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200" data-testid={`admin-payment-row-${tx.id}`}>
                  <div className={`w-2 h-2 rounded-full ${isPaid ? "bg-emerald-500" : isFailed ? "bg-rose-500" : "bg-amber-500"}`}></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-slate-800 text-sm">{Number(tx.amount || 0).toLocaleString("fr-CA")} $</span>
                      <span className="text-[10px] text-slate-500 uppercase">{tx.type === "pad_charge" ? "PAD" : "CB"}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isPaid ? "bg-emerald-100 text-emerald-700" : isFailed ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{tx.payment_status}</span>
                      <span className="text-[10px] text-slate-500">{tx.stripe_mode === "live" ? "LIVE" : "TEST"}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      <b>{tx._master_name}</b> · {tx.metadata?.period_label || ""} · {tx.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(tx)}
                    disabled={deletingId === tx.id}
                    className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50"
                    data-testid={`admin-payment-delete-${tx.id}`}
                    title="Supprimer ce log de transaction (sans toucher à Stripe)"
                  >
                    {deletingId === tx.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
